import React, { useCallback, useMemo, useState } from 'react'
import { FormatOptionLabelMeta, OptionTypeBase, ValueType, ActionMeta } from 'react-select'
import { AsyncProps } from 'react-select/async'
import { debounce } from 'lodash'
import * as t from 'io-ts'

import { request } from '@genome-web-forms/common/api'
import { MyIDUser } from '@genome-web-forms/common/auth'

import { useUser } from 'auth/Auth'
import { authGWF } from 'api/auth'
import { AsyncSelectCreatable, MemoAsyncSelect } from 'shared/components/Select'
import config from 'shared/config'
import { nullable } from 'codecs/util/nullable'

import { usePortrayalsContext } from './portrayalRowsContext'
import FormikSelect from '../Select/FormikSelect'
import ErrorMessage from 'shared/components/ErrorMessage'
import { useField, useFormikContext } from 'formik'
import { abortableLoadOptions, generatePortrayalName, getMdmIdLabel } from './utils'
import { DEFAULT_DEBOUNCE_DELAY, EMPTY_FIELD_LABEL, NEW_FIELD_LABEL } from './constants'
import { MdmIdGrid } from './Layout'
import styled from 'shared/theme'
import { PortrayalFormInProgressState } from './types'

const FEATURE_CREATABLE_CHARACTERS = false

export const CharacterOptionCodec = t.type({
    id: t.string,
    name: t.string,
    snapshot: t.null,
    mdmID: nullable(t.string),
})

export type CharacterOption = t.TypeOf<typeof CharacterOptionCodec>

type FormatedOption = {
    label: string
    uri: string
    mdmId?: string | undefined
    isNew?: boolean
}

type FormatedTalentOption = {
    label: string
    uri: string
    mdmId?: string | undefined
    isNew?: boolean
}

const getOptionLabel = (o: FormatedOption) => o.label
const getOptionValue = (o: FormatedOption) => o.uri

const formatOptions = (rowOpts: CharacterOption[]): FormatedOption[] => {
    return rowOpts.map(opt => {
        return {
            label: opt.name,
            uri: opt.id,
            mdmId: opt.mdmID || undefined,
        }
    })
}

async function fetchData<T>(
    user: MyIDUser,
    isMdmMode: boolean,
    optionsFormatter: (rowOpt: CharacterOption[]) => T[],
    text = '',
    abortCtrl: AbortController,
): Promise<T[]> {
    if (text === '') {
        return []
    }

    const url = !isMdmMode
        ? `${config.urlGWFOntology}/characters?characterName=${encodeURIComponent(text)}`
        : `${config.urlGWFOntology}/characters?mdmID=${text}`

    return request(
        t.array(CharacterOptionCodec),
        authGWF(user, {
            url: url,
            method: 'GET',
            signal: abortCtrl.signal,
        }),
    ).then(resp => {
        return optionsFormatter(resp)
    })
}

const loadOptions = (
    user: MyIDUser,
    text: string,
    isMdmMode: boolean,
    abortCtrl: AbortController,
    callback: (opts: FormatedOption[]) => any,
) => {
    fetchData(user, isMdmMode, formatOptions, text, abortCtrl).then(resp => callback(resp))
}

const CustomCharacterOption: React.FC<{
    option: FormatedOption
    meta: FormatOptionLabelMeta<FormatedOption>
}> = ({ option, meta: { context } }) => {
    if (context === 'menu') {
        return (
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{option.label}</span>
                <span style={{ fontWeight: 'bold', marginLeft: '1rem' }}>
                    {option.mdmId ? option.mdmId : EMPTY_FIELD_LABEL}
                </span>
            </div>
        )
    } else {
        return <div>{option.label}</div>
    }
}

interface FormikCharacterSearchProps<T extends OptionTypeBase> {
    name: string
    disabled: boolean
    debouncedLoadOptions: (
        user: MyIDUser,
        val: string,
        ctrl: AbortController,
        callback: (opts: T[]) => any,
    ) => any
    getOptionValue: (opt: T) => string
    getOptionLabel: (opt: T) => string
    onChange?: (value: ValueType<T>, meta: ActionMeta<T>) => void
    formatOptionLable?: (option: T, meta: FormatOptionLabelMeta<T>) => JSX.Element
    onCreateOption?(val: string): void
    isLoading?: boolean
    isMdmMode: boolean
    isCreatable: boolean
}

export const FormikCharacterSearch = <T extends OptionTypeBase>({
    name,
    disabled,
    debouncedLoadOptions,
    getOptionValue,
    getOptionLabel,
    onChange,
    formatOptionLable,
    onCreateOption,
    isLoading,
    isCreatable,
    isMdmMode,
}: FormikCharacterSearchProps<T>): React.ReactElement => {
    const user = useUser()

    const [prevCancelTokenSourceObj] = useState({
        ctrl: null,
    })

    const loadOptions = useCallback<AsyncProps<T>['loadOptions']>(
        (inputValue, callback) => {
            abortableLoadOptions<T>(
                user,
                inputValue,
                prevCancelTokenSourceObj,
                callback,
                debouncedLoadOptions,
            )
        },
        [user, prevCancelTokenSourceObj, debouncedLoadOptions],
    )

    return (
        <div>
            <FormikSelect<T>
                name={name}
                isDisabled={disabled}
                loadOptions={loadOptions}
                placeholder={'Search...'}
                cacheOptions={false}
                getOptionLabel={getOptionLabel}
                getOptionValue={getOptionValue}
                onChange={onChange}
                formatOptionLabel={formatOptionLable}
                onCreateOption={onCreateOption}
                isLoading={isLoading}
                createOptionPosition={'first'}
                isValidNewOption={inputValue => !!inputValue.length}
                formatCreateLabel={inputValue => `Add "${inputValue}" as a new character`}
                selectComponent={isCreatable && !isMdmMode ? AsyncSelectCreatable : MemoAsyncSelect}
            />
            <ErrorMessage name={name} />
        </div>
    )
}

export const FormikCharacterSearchAsCharacter: React.FC<{
    name: string
    disabled: boolean
    index: number
    isMdmMode: boolean
}> = ({ name, disabled, index, isMdmMode }) => {
    const { values, setFieldValue } = useFormikContext<PortrayalFormInProgressState>()
    const [{ value }] = useField<{ uri: string; label: string; mdmId?: string | undefined } | null>(
        name,
    )

    const handleChange = useCallback(
        (value: ValueType<FormatedOption>) => {
            if (!!value) {
                const portraysValues = [...values.portrays]
                portraysValues[index] = value as FormatedOption
                const generatedValue = generatePortrayalName(portraysValues, values.portrayedBy)
                setFieldValue('portrayal.portrayalName', generatedValue)
            }
            setFieldValue(name, value)
        },
        [values, name, index, setFieldValue],
    )

    const formatOptionLable = useCallback(
        (option: FormatedOption, meta: FormatOptionLabelMeta<FormatedOption>) => {
            return <CustomCharacterOption option={option} meta={meta} />
        },
        [],
    )

    const debouncedLoadOptionsForCharacter = useMemo(
        () =>
            debounce(
                (
                    user: MyIDUser,
                    text: string,
                    abortCtrl: AbortController,
                    callback: (opts: FormatedOption[]) => void,
                ) => loadOptions(user, text, isMdmMode, abortCtrl, callback),
                DEFAULT_DEBOUNCE_DELAY,
            ),
        [isMdmMode],
    )

    const handleCreateOption = (v: string) => {
        handleChange({
            label: v,
            uri: NEW_FIELD_LABEL,
            mdmId: undefined,
            isNew: true,
        })
    }

    return (
        <MdmIdGrid disabled={disabled}>
            {disabled ? (
                <div>{value?.label}</div>
            ) : (
                <div>
                    <FormikCharacterSearch
                        name={name}
                        disabled={disabled}
                        debouncedLoadOptions={debouncedLoadOptionsForCharacter}
                        getOptionLabel={getOptionLabel}
                        getOptionValue={getOptionValue}
                        onChange={handleChange}
                        formatOptionLable={formatOptionLable}
                        onCreateOption={handleCreateOption}
                        isMdmMode={isMdmMode}
                        isCreatable={FEATURE_CREATABLE_CHARACTERS}
                    />
                </div>
            )}
            <div>{getMdmIdLabel(value)}</div>
        </MdmIdGrid>
    )
}

export const FormikCharacterSearchAsTalent: React.FC<{
    name: string
    disabled: boolean
    index: number
    isMdmMode: boolean
}> = ({ name, disabled, index, isMdmMode }) => {
    const { values, setFieldValue } = useFormikContext<PortrayalFormInProgressState>()
    const [{ value }] = useField<{ uri: string; label: string; mdmId?: string | undefined } | null>(
        name,
    )

    const handleChange = useCallback(
        (value: ValueType<FormatedTalentOption>) => {
            if (!!value) {
                const portrayedByValues = [...values.portrayedBy]
                portrayedByValues[index] = value as FormatedTalentOption
                const generatedValue = generatePortrayalName(values.portrays, portrayedByValues)
                setFieldValue('portrayal.portrayalName', generatedValue)
            }
            setFieldValue(name, value)
        },
        [values, name, index, setFieldValue],
    )

    const formatOptionLable = useCallback(
        (option: FormatedTalentOption, meta: FormatOptionLabelMeta<FormatedTalentOption>) => {
            return <CustomCharacterOption option={option} meta={meta} />
        },
        [],
    )

    const debouncedLoadOptionsFoTalent = useMemo(
        () =>
            debounce(
                (
                    user: MyIDUser,
                    text: string,
                    abortCtrl: AbortController,
                    callback: (opts: FormatedTalentOption[]) => void,
                ) => loadOptions(user, text, isMdmMode, abortCtrl, callback),
                DEFAULT_DEBOUNCE_DELAY,
            ),
        [isMdmMode],
    )

    const handleCreateOption = (v: string) => {
        handleChange({
            label: v,
            uri: NEW_FIELD_LABEL,
            mdmId: undefined,
            isNew: true,
        })
    }

    return (
        <MdmIdGrid disabled={disabled}>
            {disabled ? (
                <div>{value?.label}</div>
            ) : (
                <div>
                    <FormikCharacterSearch
                        name={name}
                        disabled={disabled}
                        debouncedLoadOptions={debouncedLoadOptionsFoTalent}
                        getOptionLabel={getOptionLabel}
                        getOptionValue={getOptionValue}
                        onChange={handleChange}
                        formatOptionLable={formatOptionLable}
                        onCreateOption={handleCreateOption}
                        isMdmMode={isMdmMode}
                        isCreatable={false}
                    />
                </div>
            )}
            <div>{getMdmIdLabel(value)}</div>
        </MdmIdGrid>
    )
}

export const FormikRelCharacterSearch: React.FC<{
    disabled: boolean
    name: string
    isMdmMode: boolean
}> = ({ disabled, name, isMdmMode }) => {
    const formatOptionLable = useCallback(
        (option: FormatedOption, meta: FormatOptionLabelMeta<FormatedOption>) => {
            return <CustomCharacterOption option={option} meta={meta} />
        },
        [],
    )

    const debouncedLoadOptionsForCharacter = useMemo(
        () =>
            debounce(
                (
                    user: MyIDUser,
                    text: string,
                    abortCtrl: AbortController,
                    callback: (opts: FormatedOption[]) => void,
                ) => loadOptions(user, text, isMdmMode, abortCtrl, callback),
                DEFAULT_DEBOUNCE_DELAY,
            ),
        [isMdmMode],
    )

    return (
        <div>
            <FormikCharacterSearch
                name={name}
                disabled={disabled}
                debouncedLoadOptions={debouncedLoadOptionsForCharacter}
                getOptionLabel={getOptionLabel}
                getOptionValue={getOptionValue}
                formatOptionLable={formatOptionLable}
                isMdmMode={isMdmMode}
                isCreatable={false}
            />
        </div>
    )
}

interface InlineCharacterSearchProps<T extends OptionTypeBase> {
    disabled: boolean
    value: ValueType<T>
    debouncedLoadOptions: (
        user: MyIDUser,
        val: string,
        ctrl: AbortController,
        callback: (opts: T[]) => any,
    ) => any
    getOptionValue: (opt: T) => string
    getOptionLabel: (opt: T) => string
    onChange?: (value: ValueType<T>, meta: ActionMeta<T>) => void
    formatOptionLable?: (option: T, meta: FormatOptionLabelMeta<T>) => JSX.Element
}

export const InlineCharacterSearch = <T extends OptionTypeBase>({
    disabled,
    value,
    debouncedLoadOptions,
    getOptionValue,
    getOptionLabel,
    onChange,
    formatOptionLable,
}: InlineCharacterSearchProps<T>): React.ReactElement => {
    const user = useUser()

    const [prevCancelTokenSourceObj] = useState({
        ctrl: null,
    })

    const loadOptions = useCallback<AsyncProps<T>['loadOptions']>(
        (inputValue, callback) => {
            abortableLoadOptions<T>(
                user,
                inputValue,
                prevCancelTokenSourceObj,
                callback,
                debouncedLoadOptions,
            )
        },
        [user, prevCancelTokenSourceObj, debouncedLoadOptions],
    )

    return (
        <MemoAsyncSelect
            isMulti={false}
            value={value}
            loadOptions={loadOptions}
            placeholder={'Search...'}
            cacheOptions={false}
            onChange={onChange}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            menuPlacement="auto"
            isDisabled={disabled}
            formatOptionLabel={formatOptionLable}
        />
    )
}

export const InlineCharacterSearchAsCharacterBase: React.FC<{
    disabled: boolean
    index: number
    isMdmMode?: boolean
}> = ({ disabled, index, isMdmMode }) => {
    const { portrayalInEdit, setCurrentPortrayal } = usePortrayalsContext()

    const value = useMemo(() => {
        const character = portrayalInEdit?.portrays[index] || null
        return character
    }, [portrayalInEdit, index])

    const handleOnChange = useCallback(
        (opt: ValueType<FormatedOption>) => {
            // Use different type if select reset is allowed (value = null). Then CastMember cannot be used here
            const selectedOpt = opt as FormatedOption
            if (!!portrayalInEdit && !!selectedOpt) {
                let portrayalName = portrayalInEdit.portrayal.portrayalName
                let portraysValues = [...portrayalInEdit.portrays]
                portraysValues[index] = selectedOpt
                portrayalName = generatePortrayalName(portraysValues, portrayalInEdit.portrayedBy)

                setCurrentPortrayal({
                    ...portrayalInEdit,
                    portrays: portraysValues,
                    portrayal: { ...portrayalInEdit.portrayal, portrayalName },
                })
            }
        },
        [portrayalInEdit, index, setCurrentPortrayal],
    )

    const formatOptionLable = useCallback(
        (option: FormatedOption, meta: FormatOptionLabelMeta<FormatedOption>) => {
            return <CustomCharacterOption option={option} meta={meta} />
        },
        [],
    )

    const debouncedLoadOptionsForCharacter = useMemo(
        () =>
            debounce(
                (
                    user: MyIDUser,
                    text: string,
                    abortCtrl: AbortController,
                    callback: (opts: FormatedOption[]) => void,
                ) => loadOptions(user, text, isMdmMode || false, abortCtrl, callback),
                DEFAULT_DEBOUNCE_DELAY,
            ),
        [isMdmMode],
    )

    return (
        <InlineCharacterSearch
            disabled={disabled}
            value={value}
            debouncedLoadOptions={debouncedLoadOptionsForCharacter}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            onChange={handleOnChange}
            formatOptionLable={formatOptionLable}
        />
    )
}

export const InlineCharacterSearchAsCharacter = styled(InlineCharacterSearchAsCharacterBase)`
    display: flex;
    flex-basis: 180px;
    flex-direction: column;
`

export const InlineCharacterSearchAsTalentBase: React.FC<{
    disabled: boolean
    index: number
    isMdmMode?: boolean
}> = ({ disabled, index, isMdmMode }) => {
    const { portrayalInEdit, setCurrentPortrayal } = usePortrayalsContext()

    const value = useMemo(() => {
        const talent = portrayalInEdit?.portrayedBy[index] || null
        return talent
    }, [portrayalInEdit, index])

    const handleOnChange = useCallback(
        (opt: ValueType<FormatedTalentOption>) => {
            // Use different type if select reset is allowed (value = null). Then CastMember cannot be used here
            const selectedOpt = opt as FormatedTalentOption
            if (!!portrayalInEdit && !!selectedOpt) {
                let portrayalName = portrayalInEdit.portrayal.portrayalName
                let portrayedByValues = [...portrayalInEdit.portrayedBy]
                portrayedByValues[index] = selectedOpt
                portrayalName = generatePortrayalName(portrayalInEdit.portrays, portrayedByValues)
                setCurrentPortrayal({
                    ...portrayalInEdit,
                    portrayedBy: portrayedByValues,
                    portrayal: { ...portrayalInEdit.portrayal, portrayalName },
                })
            }
        },
        [portrayalInEdit, index, setCurrentPortrayal],
    )

    const formatOptionLable = useCallback(
        (option: FormatedTalentOption, meta: FormatOptionLabelMeta<FormatedTalentOption>) => {
            return <CustomCharacterOption option={option} meta={meta} />
        },
        [],
    )

    const debouncedLoadOptionsForTalent = useMemo(
        () =>
            debounce(
                (
                    user: MyIDUser,
                    text: string,
                    abortCtrl: AbortController,
                    callback: (opts: FormatedTalentOption[]) => void,
                ) => loadOptions(user, text, isMdmMode || false, abortCtrl, callback),
                DEFAULT_DEBOUNCE_DELAY,
            ),
        [isMdmMode],
    )

    return (
        <InlineCharacterSearch
            disabled={disabled}
            value={value}
            debouncedLoadOptions={debouncedLoadOptionsForTalent}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            onChange={handleOnChange}
            formatOptionLable={formatOptionLable}
        />
    )
}

export const InlineCharacterSearchAsTalent = styled(InlineCharacterSearchAsTalentBase)`
    display: flex;
    flex-basis: 180px;
    flex-direction: column;
`
